import { NOC_MODULES, type NocModule } from "@seagames/contracts";
import { useSyncExternalStore } from "react";
import { ApiError } from "../api/client";
import { type StageLock, nocModuleForPath } from "./access";

/**
 * The last 403 STAGE_LOCKED the API answered, and the page it answered on.
 *
 * The route guard already keeps a NOC off a locked page using `/auth/me`;
 * this catches the rest — the stage moved back while the page was open, or a
 * page reached a module the GMS mapped differently. The shell shows the same
 * "not open yet" screen for that page — when the refusal is for the page's
 * own module (`apiLockTakesOver`). STAGE_LOCKED is not a credential
 * verdict (contracts `isCredentialVerdict`), so the client never signs anyone
 * out over it; this store only changes what is drawn.
 *
 * Fed by the query client's caches (lib/api/query-client.ts) — every query
 * and mutation, no per-page wiring.
 */
export interface ApiStageLock extends StageLock {
	/** The page that was open when the API refused. Another page clears it. */
	pathname: string;
	/** Bumps per refusal, so the shell re-reads `/auth/me` once per new one. */
	seq: number;
}

let current: ApiStageLock | null = null;
let seq = 0;
const listeners = new Set<() => void>();

const emit = () => {
	for (const l of listeners) l();
};

const asModule = (v: unknown): NocModule => ((NOC_MODULES as readonly unknown[]).includes(v) ? (v as NocModule) : "dashboard");
const asStage = (v: unknown): number => {
	const n = Number(v);
	return Number.isInteger(n) && n > 0 ? n : 0;
};

/** Record a STAGE_LOCKED from any API call. Anything else is ignored. */
export function noteStageLocked(err: unknown): void {
	if (!(err instanceof ApiError) || err.code !== "STAGE_LOCKED") return;
	const d = err.details ?? {};
	current = {
		module: asModule(d["module"]),
		opensAtStage: asStage(d["opens_at_stage"]),
		stage: asStage(d["stage"]),
		pathname: window.location.pathname,
		seq: ++seq,
	};
	emit();
}

export function clearStageLocked(): void {
	if (current === null) return;
	current = null;
	emit();
}

function subscribe(cb: () => void) {
	listeners.add(cb);
	return () => {
		listeners.delete(cb);
	};
}

/**
 * Whether the API's refusal replaces the page at `pathname`: only when it
 * was answered on this page AND locks this page's OWN module (the path map).
 * A secondary read of another module (Accommodation's helpdesk counts at
 * stage 2) fails quietly in its own widget; the open page stays.
 */
export function apiLockTakesOver(lock: Pick<ApiStageLock, "pathname" | "module"> | null, pathname: string): boolean {
	return !!lock && lock.pathname === pathname && lock.module === nocModuleForPath(pathname);
}

/** The API's lock for `pathname` when it takes the page over (`apiLockTakesOver`), or null. */
export function useApiStageLock(pathname: string): ApiStageLock | null {
	const lock = useSyncExternalStore(subscribe, () => current, () => current);
	return lock && apiLockTakesOver(lock, pathname) ? lock : null;
}
